import React from 'react';
import { motion } from 'framer-motion';
import { MicIcon, BrainCircuitIcon, TrendingUpIcon, NetworkIcon, GamepadIcon } from 'lucide-react';
import { SlideLayout, itemVariants } from '../ui/SlideLayout';
export function UseCasesSlide() {
  return <SlideLayout data-id="element-469" data-mp="2m5yybaivm1kx">
      <motion.div variants={itemVariants} className="mb-12 text-center" data-id="element-470" data-mp="2h4rq5actn0dq">
        <h2 className="text-4xl md:text-5xl font-bold text-brand-text mb-4" data-id="element-471" data-mp="2h6vsvoyzhtcf">
          Built for Production Workloads
        </h2>
        <p className="text-xl text-brand-text-dim" data-id="element-472" data-mp="27f87o5t0t8je">
          Latency-Sensitive & Cost-Sensitive
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-6 max-w-5xl mx-auto" data-id="element-473" data-mp="22gwy7w6n101p">
        {/* Top Row - 3 Cards */}
        <motion.div variants={itemVariants} className="md:col-span-2 bg-brand-bg-card border border-brand-border hover:border-brand-teal/50 transition-colors rounded-2xl p-6 relative overflow-hidden group" data-id="element-474" data-mp="1yfl7fnc128rx">
          <div className="absolute top-0 right-0 w-24 h-24 bg-brand-teal/5 blur-2xl rounded-full group-hover:bg-brand-teal/10 transition-colors" data-id="element-475" data-mp="1oighrdiz3v6d" />
          <MicIcon className="w-8 h-8 text-brand-teal mb-4 relative z-10" data-id="element-476" data-mp="2oak4hq6y525t" />
          <h3 className="text-xl font-bold text-brand-text mb-2 relative z-10" data-id="element-477" data-mp="2ainhu1o1rkgt">
            Real-Time Voice & Chat
          </h3>
          <p className="text-sm text-brand-text-dim relative z-10" data-id="element-478" data-mp="2fi02cfrg4fg8">
            Sub-100ms time-to-first-token for natural conversational flow.
            Deterministic latency eliminates spikes that break interactions.
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="md:col-span-2 bg-brand-bg-card border border-brand-border hover:border-brand-teal/50 transition-colors rounded-2xl p-6 relative overflow-hidden group" data-id="element-479" data-mp="2t8ll84gwgpe4">
          <div className="absolute top-0 right-0 w-24 h-24 bg-brand-teal/5 blur-2xl rounded-full group-hover:bg-brand-teal/10 transition-colors" data-id="element-480" data-mp="1hsetfs2x43ts" />
          <BrainCircuitIcon className="w-8 h-8 text-brand-teal mb-4 relative z-10" data-id="element-481" data-mp="1mq6o2tuy641o" />
          <h3 className="text-xl font-bold text-brand-text mb-2 relative z-10" data-id="element-482" data-mp="12sj5kne43l1h">
            Autonomous AI Agents
          </h3>
          <p className="text-sm text-brand-text-dim relative z-10" data-id="element-483" data-mp="ty4p4hhui4gs">
            High call volume, tight cost budgets. Agents making thousands of
            calls need the lowest $/M tokens without sacrificing speed.
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="md:col-span-2 bg-brand-bg-card border border-brand-border hover:border-brand-teal/50 transition-colors rounded-2xl p-6 relative overflow-hidden group" data-id="element-484" data-mp="k1i9q88ag5m5">
          <div className="absolute top-0 right-0 w-24 h-24 bg-brand-teal/5 blur-2xl rounded-full group-hover:bg-brand-teal/10 transition-colors" data-id="element-485" data-mp="605slthe5xyo" />
          <TrendingUpIcon className="w-8 h-8 text-brand-teal mb-4 relative z-10" data-id="element-486" data-mp="25c8hxkcp5mk9" />
          <h3 className="text-xl font-bold text-brand-text mb-2 relative z-10" data-id="element-487" data-mp="20dxafg9vjo57">
            Financial Analytics
          </h3>
          <p className="text-sm text-brand-text-dim relative z-10" data-id="element-488" data-mp="27x6kzcu49svu">
            Speed and determinism matter. Low-jitter inference for real-time
            trend analysis where milliseconds translate to dollars.
          </p>
        </motion.div>

        {/* Bottom Row - 2 Cards Centered */}
        <motion.div variants={itemVariants} className="md:col-start-2 md:col-span-2 bg-brand-bg-card border border-brand-border hover:border-brand-teal/50 transition-colors rounded-2xl p-6 relative overflow-hidden group" data-id="element-489" data-mp="2mlavhtj0my3j">
          <div className="absolute top-0 right-0 w-24 h-24 bg-brand-teal/5 blur-2xl rounded-full group-hover:bg-brand-teal/10 transition-colors" data-id="element-490" data-mp="1yx1fx9ceb6ku" />
          <NetworkIcon className="w-8 h-8 text-brand-teal mb-4 relative z-10" data-id="element-491" data-mp="22zegzkg76gba" />
          <h3 className="text-xl font-bold text-brand-text mb-2 relative z-10" data-id="element-492" data-mp="3acycnjqh6usm">
            Multi-Agent Orchestration
          </h3>
          <p className="text-sm text-brand-text-dim relative z-10" data-id="element-493" data-mp="3ebytiwpe7ncz">
            Frameworks like AutoGen routing to cheaper open-source models.
            Isochron makes them fast enough for tight loops.
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="md:col-span-2 bg-brand-bg-card border border-brand-border hover:border-brand-teal/50 transition-colors rounded-2xl p-6 relative overflow-hidden group" data-id="element-494" data-mp="3muixtt8u7e2j">
          <div className="absolute top-0 right-0 w-24 h-24 bg-brand-teal/5 blur-2xl rounded-full group-hover:bg-brand-teal/10 transition-colors" data-id="element-495" data-mp="3vufi7io71qrs" />
          <GamepadIcon className="w-8 h-8 text-brand-teal mb-4 relative z-10" data-id="element-496" data-mp="3qujkg163avko" />
          <h3 className="text-xl font-bold text-brand-text mb-2 relative z-10" data-id="element-497" data-mp="9f4kb9iwc4be">
            Game AI & NPCs
          </h3>
          <p className="text-sm text-brand-text-dim relative z-10" data-id="element-498" data-mp="igkfwb51y1vk">
            Localized inference brains for complex NPC behavior. Low-latency,
            low-cost inference that runs alongside game logic.
          </p>
        </motion.div>
      </div>
    </SlideLayout>;
}